import { Monitor, Moon, Sun } from 'lucide-react';
import { type KeyboardEvent, useRef } from 'react';
import { useTheme } from '../../lib/theme-context.tsx';
import { THEME_PREFERENCES, type ThemePreference } from '../../lib/theme.ts';

const OPTIONS: Record<ThemePreference, { label: string; Icon: typeof Sun }> = {
  light: { label: 'Light', Icon: Sun },
  dark: { label: 'Dark', Icon: Moon },
  system: { label: 'System', Icon: Monitor },
};

/** Light · Dark · System (brand/components/ThemeToggle): a radio group, arrow keys move the choice. */
export function ThemeToggle() {
  const { preference, setPreference, saveError } = useTheme();
  const refs = useRef<Partial<Record<ThemePreference, HTMLButtonElement | null>>>({});

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const step =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? -1
          : 0;
    if (step === 0) return;
    e.preventDefault();
    const i = THEME_PREFERENCES.indexOf(preference);
    const next =
      THEME_PREFERENCES[(i + step + THEME_PREFERENCES.length) % THEME_PREFERENCES.length];
    if (!next) return;
    setPreference(next);
    refs.current[next]?.focus();
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        role="radiogroup"
        aria-label="Theme"
        className="fl-segmented self-start"
        onKeyDown={onKeyDown}
        data-testid="theme-toggle"
      >
        {THEME_PREFERENCES.map((value) => {
          const { label, Icon } = OPTIONS[value];
          const checked = preference === value;
          return (
            <button
              key={value}
              ref={(el) => {
                refs.current[value] = el;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              className="fl-segmented__opt"
              onClick={() => setPreference(value)}
            >
              <Icon size={16} strokeWidth={1.75} aria-hidden />
              {label}
            </button>
          );
        })}
      </div>
      {saveError ? (
        <p role="status" className="footnote text-ink-muted">
          {saveError}
        </p>
      ) : null}
    </div>
  );
}

/** The quick sun/moon switch in the desktop account row: flips between Light and Dark. */
export function ThemeSwitch() {
  const { theme, setPreference } = useTheme();
  const toDark = theme === 'light';
  return (
    <button
      type="button"
      className="fl-icon-btn text-ink-muted hover:bg-surface-raised focus-visible:outline-2 focus-visible:outline-focus-ring"
      aria-label={toDark ? 'Switch to dark mode' : 'Switch to light mode'}
      title={toDark ? 'Dark mode' : 'Light mode'}
      onClick={() => setPreference(toDark ? 'dark' : 'light')}
      data-testid="theme-switch"
    >
      {toDark ? (
        <Moon size={20} strokeWidth={1.75} aria-hidden />
      ) : (
        <Sun size={20} strokeWidth={1.75} aria-hidden />
      )}
    </button>
  );
}
